/**
 * <load-more-button> — progressive "Load more" / infinite scroll for the
 * collection grid.
 *
 * Fetches the next paginated page, extracts its product <li> items and appends
 * them to #product-grid. In "infinite" mode it auto-loads as the element nears
 * the viewport (disabled for reduced-motion users, who keep the button).
 *
 * SEO-friendly: the real numbered pages still exist and are crawlable via the
 * paginated URLs; this only enhances the on-page browsing experience.
 */
if (!customElements.get('load-more-button')) {
  class LoadMoreButton extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('[data-load-more-trigger]');
      this.spinner = this.querySelector('.loading__spinner');
      this.loading = false;
    }

    connectedCallback() {
      this.grid = document.getElementById('product-grid');
      this.mode = this.dataset.mode;

      if (this.button) {
        this.button.addEventListener('click', () => this.loadNext());
      }

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.mode === 'infinite' && 'IntersectionObserver' in window && !reduceMotion) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.loadNext();
            });
          },
          { rootMargin: '600px 0px' }
        );
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    loadNext() {
      if (this.loading || !this.dataset.url || !this.grid) return;
      this.loading = true;
      this.setAttribute('aria-busy', 'true');
      if (this.spinner) this.spinner.classList.remove('hidden');
      if (this.button) this.button.setAttribute('disabled', 'disabled');

      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const newGrid = doc.getElementById('product-grid');
          if (newGrid) {
            const fragment = document.createDocumentFragment();
            newGrid.querySelectorAll(':scope > li').forEach((li) => fragment.appendChild(li));
            this.grid.appendChild(fragment);

            // Re-arm scroll-reveal animations for the freshly added cards.
            if (typeof initializeScrollAnimationTrigger === 'function') {
              try { initializeScrollAnimationTrigger(this.grid); } catch (e) { /* noop */ }
            }
          }

          // Advance to the next page, or retire the control when finished.
          const nextControl = doc.querySelector('load-more-button');
          const nextUrl = nextControl ? nextControl.getAttribute('data-url') : null;
          if (nextUrl) {
            this.dataset.url = nextUrl;
          } else {
            if (this.observer) this.observer.disconnect();
            this.remove();
          }
        })
        .catch((e) => console.error('Load more failed:', e))
        .finally(() => {
          this.loading = false;
          this.removeAttribute('aria-busy');
          if (this.spinner) this.spinner.classList.add('hidden');
          if (this.button) this.button.removeAttribute('disabled');
        });
    }
  }

  customElements.define('load-more-button', LoadMoreButton);
}
